import type { Chart } from 'klinecharts'
import type { CandleData, OrderData, OrderEventData } from '../types/chartData'
import type { FormingBarState } from '../utils/formingBarBuffer'
import { closeLegsByKey, closesByKey, pairKeyOf, tradePips, type PairKey } from '../utils/orderPairing'
import { PENDING_TYPE_ABBR, restingOrdersAt, type PlacedOrderEvent } from '../utils/pendingOrders'
import { getTimeframeSeconds } from '../../utils/chartUtils'
import {
  FILL_CROSS,
  ORDER_LINES,
  PENDING_ORDER_LINE,
  TRADE_CONNECTOR,
  TRADE_MARKER,
  type OrderLinesExtendData,
  type PendingLineExtendData,
} from './backtestChartOverlays'

/**
 * The replay's trade overlays — arrows grouped per bar and side, entry→exit connectors, and the
 * working entry / stop-loss / take-profit lines of positions that are still open — drawn onto any
 * klinecharts instance.
 *
 * One implementation, two charts: the Backtest tab's BacktestChart, and the Workbench canvas
 * while a walkthrough is shown. The overlay templates themselves are in backtestChartOverlays.ts
 * (registerBacktestChartOverlays must have run). Everything here works in unix SECONDS; the
 * conversion to klinecharts' milliseconds happens at the createOverlay calls.
 */

/**
 * Entry→exit connector for a closed backtest order — the same dashed segment the manual Buy/Sell
 * entry tool draws between an entry and its exit, but PnL-coloured like the exit arrows. Returns
 * null when the order carries no entry data (open positions, legacy events).
 */
export function backtestOrderConnector(o: {
  eventType: 'opened' | 'closed'
  time: number
  price: number
  entryTime?: number
  entryPrice?: number
  pnl?: number
}): { points: Array<{ timestamp: number; value: number }>; color: string } | null {
  if (o.eventType !== 'closed' || o.entryTime == null || o.entryPrice == null) return null
  return {
    points: [
      { timestamp: o.entryTime * 1000, value: o.entryPrice },
      { timestamp: o.time * 1000, value: o.price },
    ],
    color: (o.pnl ?? 0) > 0 ? '#00C853' : '#FF1744',
  }
}

/**
 * Ceiling for markers AND connectors together: klinecharts walks every overlay
 * on each repaint, and this chart repaints on every replay tick. Markers are
 * spent first (they're the primary signal), connectors take the remainder.
 * Newest win. Windowed orders mean neither normally binds.
 */
export const MAX_OVERLAYS = 800

const MARKER_NEUTRAL = '#9E9E9E'
const MARKER_WIN = '#00C853'
const MARKER_LOSS = '#FF1744'

/**
 * The walkthrough's marks, as the prototype draws them (PROD-3133). Every fill is coloured by its
 * own side, MetaTrader's way: a long is bought to open (teal, pointing up) and sold to close (red,
 * pointing down), a short the mirror. The connector wears the TRADE's direction, mixed 78% with
 * white so it stays quieter than the arrows it joins. Direction, not outcome: it is true from the
 * first bar, so a replay never tells the result early.
 */
const WT_BUY = '#26a69a'
const WT_SELL = '#f23645'
const WT_CONNECTOR_SIZE = 2
const WT_TIP_GAP = 2

function mixWithWhite(hex: string, pct: number): string {
  const n = parseInt(hex.slice(1), 16)
  const mix = (v: number) => Math.round(v * pct + 255 * (1 - pct))
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`
}

export interface WalkthroughMarks {
  cross: string
  halo: string
}

/** The side a fill traded: a closed event carries its POSITION's type, and closing a long sells. */
function fillSide(o: OrderData): 'buy' | 'sell' {
  const positionBuy = o.type === 'BUY'
  if (o.eventType === 'closed') return positionBuy ? 'sell' : 'buy'
  return positionBuy ? 'buy' : 'sell'
}

/** Index of the last bar at or before `ts`, or -1 when `ts` precedes the window. */
export function findBarIndex(candles: CandleData[], ts: number): number {
  let lo = 0
  let hi = candles.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (candles[mid].time <= ts) lo = mid + 1
    else hi = mid
  }
  return lo - 1
}

interface MarkerSpec {
  key: string
  barTime: number
  value: number
  dir: 'up' | 'down'
  color: string
  text: string
  /** The orders grouped on this arrow, for a click to name. */
  orders: OrderData[]
  /** Scale-ins / partial exits grouped on this arrow (the walkthrough only). */
  activity: OrderEventData[]
  /** The walkthrough's MetaTrader arrow; absent on the Backtest tab. */
  mt5?: boolean
}

type NettingMark = Extract<OrderEventData, { kind: 'order_filled' | 'position_reduced' }>

/**
 * A single order's realised PnL, or null while the outcome is not known yet.
 *
 * `closes` is the walkthrough's entry-time pairing. With it, an `opened` event can be asked
 * how its position ENDED, which is what lets an entry arrow stay grey while the trade runs
 * and flip to win/loss the moment it closes — the trader watching a replay should not be
 * told the answer at the point the code committed to the trade.
 *
 * Without it (the Backtest tab), an `opened` event has no outcome and never gains one, which
 * is exactly the behaviour that shipped.
 */
function orderOutcome(o: OrderData, closes: Map<PairKey, OrderData> | null): number | null {
  if (o.eventType === 'closed') return o.pnl ?? 0
  if (!closes) return null
  const key = pairKeyOf(o)
  const close = key == null ? undefined : closes.get(key)
  return close ? (close.pnl ?? 0) : null
}

/**
 * Group orders onto bars and resolve each group's arrow. Rules carried over from
 * the lightweight-charts implementation: one marker per (bar, side); grey while
 * nothing is closed or when a group mixes outcomes, otherwise PnL-coloured; a
 * count badge once a group holds more than one order.
 */
function buildMarkers(
  candles: CandleData[],
  orders: OrderData[],
  timeframe: string,
  forming: FormingBarState | null = null,
  closes: Map<PairKey, OrderData> | null = null,
  activity: OrderEventData[] = [],
  wt: WalkthroughMarks | null = null,
): MarkerSpec[] {
  // Scale-ins and partial exits: the netting events that change a running position without
  // opening or closing one. Drawn as their own small arrows ("+" / "−") so the entry/exit
  // arrows keep meaning what they always meant.
  const marks = activity.filter(
    (a): a is NettingMark =>
      (a.kind === 'order_filled' && a.effect === 'increase') || a.kind === 'position_reduced',
  )
  if ((candles.length === 0 && forming === null) || (orders.length === 0 && marks.length === 0)) return []

  const tfSec = getTimeframeSeconds(timeframe)
  const lastBarTime = forming !== null ? forming.time : candles[candles.length - 1].time
  const lastBarEnd = lastBarTime + tfSec
  const groups = new Map<string, { bar: CandleData; side: 'buy' | 'sell'; orders: OrderData[] }>()

  for (const order of orders) {
    if (order.time >= lastBarEnd) continue
    // The forming bar is not part of `candles`, so an order at or after its start
    // belongs to it. Taking the bar object rather than an index is what lets that
    // work without splicing it into the array on every animation frame.
    const bar =
      forming !== null && order.time >= forming.time
        ? forming
        : candles[findBarIndex(candles, order.time)]
    // findBarIndex returns -1 for an order earlier than the loaded window.
    if (!bar) continue
    const side = wt ? fillSide(order) : order.type === 'BUY' ? 'buy' : 'sell'
    const key = wt ? `${bar.time}:${side}:${order.eventType}:${order.orderId}` : `${bar.time}:${side}`
    const group = groups.get(key)
    if (group) group.orders.push(order)
    else groups.set(key, { bar, side, orders: [order] })
  }

  const markGroups = new Map<string, { bar: CandleData; kind: NettingMark['kind']; dir: 'up' | 'down'; marks: NettingMark[] }>()
  for (const mark of marks) {
    if (mark.time >= lastBarEnd) continue
    const bar =
      forming !== null && mark.time >= forming.time ? forming : candles[findBarIndex(candles, mark.time)]
    if (!bar) continue
    // A scale-in points the position's way; a partial exit is a fill the other way.
    const dir: 'up' | 'down' =
      mark.kind === 'order_filled' ? (mark.side === 'BUY' ? 'up' : 'down') : mark.side === 'BUY' ? 'down' : 'up'
    const key = `${bar.time}:${mark.kind}:${dir}`
    const group = markGroups.get(key)
    if (group) group.marks.push(mark)
    else markGroups.set(key, { bar, kind: mark.kind, dir, marks: [mark] })
  }

  const specs: MarkerSpec[] = []
  for (const { bar, side, orders: grouped } of groups.values()) {
    const resolved = grouped
      .map((o) => orderOutcome(o, closes))
      .filter((pnl): pnl is number => pnl !== null)
    const color = wt
      ? side === 'buy'
        ? WT_BUY
        : WT_SELL
      : resolved.length === 0
        ? MARKER_NEUTRAL
        : resolved.length === 1 && grouped.length === 1
          ? resolved[0] > 0
            ? MARKER_WIN
            : MARKER_LOSS
          : MARKER_NEUTRAL
    const dir = side === 'buy' ? 'up' : 'down'
    const text = grouped.length > 1 ? String(grouped.length) : ''
    // Anchor below the low / above the high, reproducing belowBar / aboveBar. The walkthrough
    // does the same (MetaTrader's way, as the prototype's v6 draws it): the arrow stays clear of
    // the candle, and its × marks the exact fill price.
    const value = side === 'buy' ? bar.low : bar.high
    specs.push({
      // The key encodes everything that affects the drawing, so an open→closed
      // recolour replaces exactly one overlay and nothing else churns. The anchor
      // is included because a marker on the still-forming bar has to follow that
      // bar's low/high as they extend; a closed bar's never move, so this costs
      // nothing for the overwhelming majority.
      key: `${bar.time}:${dir}:${color}:${text}:${value}:${wt ? `mt5:${grouped[0].eventType}:${grouped[0].orderId}` : ''}`,
      barTime: bar.time,
      value,
      dir,
      color,
      text,
      orders: grouped,
      activity: [],
      ...(wt ? { mt5: true } : {}),
    })
  }
  for (const { bar, kind, dir, marks: grouped } of markGroups.values()) {
    const pnls = grouped.map((m) => (m.kind === 'position_reduced' ? m.pnl : null))
    const color =
      kind === 'position_reduced' && grouped.length === 1 && pnls[0] != null
        ? pnls[0] > 0
          ? MARKER_WIN
          : MARKER_LOSS
        : MARKER_NEUTRAL
    const glyph = kind === 'order_filled' ? '+' : '−'
    const text = grouped.length > 1 ? `${glyph}${grouped.length}` : glyph
    const value = dir === 'up' ? bar.low : bar.high
    specs.push({
      key: `act:${bar.time}:${kind}:${dir}:${color}:${text}:${value}`,
      barTime: bar.time,
      value,
      dir,
      color,
      text,
      orders: [],
      activity: grouped,
    })
  }

  specs.sort((a, b) => a.barTime - b.barTime)
  return specs.length > MAX_OVERLAYS ? specs.slice(specs.length - MAX_OVERLAYS) : specs
}

interface ConnectorSpec {
  key: string
  /** Sort/prune anchor: the exit, i.e. when the connector becomes drawable. */
  exitTime: number
  points: Array<{ timestamp: number; value: number }>
  color: string
  size?: number
  tipGap?: number
  long?: boolean
  fromExit?: boolean
  /** The closing event, for the walkthrough's hover. */
  order: OrderData
}

/**
 * Entry→exit connectors for closed orders, mapped through `backtestOrderConnector`
 * so every chart draws provably the same thing.
 *
 * `budget` is whatever the markers left of MAX_OVERLAYS.
 *
 * A connector is only built once BOTH endpoints' bars are loaded. klinecharts
 * resolves an out-of-range timestamp to the nearest bar, so an entry earlier
 * than the loaded window would bend the line flat onto the left edge. Skipped
 * orders materialize on a later pass, once extendWindow('left') reaches their
 * entry bar — the same deferral the Workbench does. This matters because
 * useGraphCandleWindow prunes orders by EXIT time, so an order can outlive the
 * bar its entry sits on.
 */
function buildConnectors(
  candles: CandleData[],
  orders: OrderData[],
  timeframe: string,
  budget: number,
  forming: FormingBarState | null = null,
  wt: WalkthroughMarks | null = null,
): ConnectorSpec[] {
  if (budget <= 0 || candles.length === 0 || orders.length === 0) return []

  const firstBarTime = candles[0].time
  // Unlike a marker, a connector's endpoints are real fill prices, so nothing
  // about it moves as the forming bar grows — it only needs the extended end so
  // an exit inside that bar becomes drawable.
  const lastBarTime = forming !== null ? forming.time : candles[candles.length - 1].time
  const lastBarEnd = lastBarTime + getTimeframeSeconds(timeframe)

  // A position closed in parts joins its exits in order, as the prototype does: entry to the first
  // exit, that exit to the next. One closed in one go has a single segment, entry to exit.
  const legs = wt ? closeLegsByKey(orders) : null

  const specs: ConnectorSpec[] = []
  for (const order of orders) {
    const conn = backtestOrderConnector(order)
    if (!conn) continue // not a closed order, or no entry data
    if (order.entryTime == null) continue
    if (order.entryTime < firstBarTime || order.time >= lastBarEnd) continue
    const color = wt ? mixWithWhite(order.type === 'BUY' ? WT_BUY : WT_SELL, 0.78) : conn.color
    const key = pairKeyOf(order)
    const mine = (key != null && legs?.get(key)) || []
    const prev = mine[mine.indexOf(order) - 1]
    if (prev) conn.points[0] = { timestamp: prev.time * 1000, value: prev.price }
    specs.push({
      // orderId is unique and stable; folding the colour in means a PnL
      // correction replaces exactly this one overlay.
      key: `${order.orderId}:${color}`,
      exitTime: order.time,
      points: conn.points,
      color,
      order,
      ...(wt ? { size: WT_CONNECTOR_SIZE, tipGap: WT_TIP_GAP, long: order.type === 'BUY', fromExit: !!prev } : {}),
    })
  }

  specs.sort((a, b) => a.exitTime - b.exitTime)
  return specs.length > budget ? specs.slice(specs.length - budget) : specs
}

interface CrossSpec {
  key: string
  time: number
  point: { timestamp: number; value: number }
  order: OrderData
}

/**
 * The walkthrough's × on every revealed fill, entry and exit, at its exact price. Same window
 * rule as the markers: a fill on a bar that is not loaded is left for a later pass.
 */
function buildCrosses(
  candles: CandleData[],
  orders: OrderData[],
  timeframe: string,
  budget: number,
  forming: FormingBarState | null = null,
): CrossSpec[] {
  if (budget <= 0 || (candles.length === 0 && forming === null) || orders.length === 0) return []
  const firstBarTime = candles.length > 0 ? candles[0].time : forming!.time
  const lastBarTime = forming !== null ? forming.time : candles[candles.length - 1].time
  const lastBarEnd = lastBarTime + getTimeframeSeconds(timeframe)
  const specs: CrossSpec[] = []
  for (const o of orders) {
    if (o.time < firstBarTime || o.time >= lastBarEnd) continue
    specs.push({
      key: `${o.eventType}:${o.orderId}:${o.time}:${o.price}`,
      time: o.time,
      point: { timestamp: o.time * 1000, value: o.price },
      order: o,
    })
  }
  specs.sort((a, b) => a.time - b.time)
  return specs.length > budget ? specs.slice(specs.length - budget) : specs
}

interface OrderLinesSpec {
  key: string
  entryTime: number
  points: Array<{ timestamp: number; value: number }>
  dir: 'buy' | 'sell'
  entryLabel: string
  slLabel: string
  tpLabel: string
  /** The position's number as the trades pane calls it, when the caller numbers positions. */
  ticket: number | null
}

/**
 * The working lines of every position that is OPEN as of the reveal cursor.
 *
 * A position is open when its entry has been revealed and its close has not, so the set
 * shrinks by one the instant a trade closes and its lines are simply no longer wanted —
 * which is what makes the reconcile delete them without any teardown code of its own. That
 * "the stop-loss is on the chart exactly while the stop-loss is real" property is the whole
 * reason the lines exist: a chart littered with the stops of finished trades protects
 * nothing and reads as if the positions were still live.
 *
 * The x anchor barely matters — the overlay draws its rules from pane edge to pane edge —
 * but klinecharts snaps an out-of-range timestamp to the nearest bar, so anchoring on a
 * loaded bar keeps the engine from having to guess.
 */
function buildOrderLines(
  candles: CandleData[],
  orders: OrderData[],
  closes: Map<PairKey, OrderData>,
  pipSize: number | null,
  budget: number,
  activity: OrderEventData[] = [],
  tickets: ReadonlyMap<PairKey, number> | null = null,
): OrderLinesSpec[] {
  if (budget <= 0 || candles.length === 0 || orders.length === 0) return []

  const firstBarTime = candles[0].time
  const lastBarTime = candles[candles.length - 1].time

  // The latest scale-in per position moves the average entry; the latest scale-in OR stop move
  // (engine API 3's position_modified) sets the stops. The lines must show the levels that are
  // real now, not the first fill's. "Latest" is by (time, event id): a modify on the scale-in's
  // own bar lands after it.
  const latestEntry = new Map<number, Extract<OrderEventData, { kind: 'position_increased' }>>()
  const latestStops = new Map<number, Extract<OrderEventData, { kind: 'position_increased' | 'position_modified' }>>()
  const later = (a: OrderEventData, b: OrderEventData | undefined) =>
    !b || a.time > b.time || (a.time === b.time && Number(a.eventId) >= Number(b.eventId))
  for (const a of activity) {
    if (a.kind === 'position_increased') {
      if (later(a, latestEntry.get(a.ticket))) latestEntry.set(a.ticket, a)
      if (later(a, latestStops.get(a.ticket))) latestStops.set(a.ticket, a)
    } else if (a.kind === 'position_modified') {
      if (later(a, latestStops.get(a.ticket))) latestStops.set(a.ticket, a)
    }
  }

  const pipLabel = (role: string, entry: number, level: number, type: 'BUY' | 'SELL') => {
    if (!pipSize) return role
    const pips = tradePips(type, entry, level, pipSize)
    if (pips == null) return role
    return `${role} · ${Math.abs(Math.round(pips))} pips`
  }

  const specs: OrderLinesSpec[] = []
  for (const order of orders) {
    if (order.eventType !== 'opened') continue
    const key = pairKeyOf(order)
    if (key != null && closes.has(key)) continue // already closed as far as the playhead knows
    const anchor = Math.min(Math.max(order.time, firstBarTime), lastBarTime)
    const inc = order.ticket != null ? latestEntry.get(order.ticket) : undefined
    const stops = order.ticket != null ? latestStops.get(order.ticket) : undefined
    const entry = inc ? inc.entryPrice : order.price
    const sl = (stops ? stops.sl : order.sl) || null
    const tp = (stops ? stops.tp : order.tp) || null
    const ticket = tickets && key != null ? (tickets.get(key) ?? null) : null
    specs.push({
      // Everything that affects the drawing is in the key, so a broker-side stop move
      // replaces exactly this one overlay and no other trade's lines repaint.
      key: `${order.orderId}:${entry}:${sl ?? '-'}:${tp ?? '-'}`,
      ticket,
      entryTime: order.time,
      // Three points, always in this order. A null level still occupies its slot as the
      // entry price so the point count is stable; the overlay's own figure list is what
      // decides not to draw it (see `hasSl` / `hasTp` in extendData's labels).
      points: [
        { timestamp: anchor * 1000, value: entry },
        { timestamp: anchor * 1000, value: sl ?? entry },
        { timestamp: anchor * 1000, value: tp ?? entry },
      ],
      dir: order.type === 'BUY' ? 'buy' : 'sell',
      entryLabel: order.type === 'BUY' ? 'Buy' : 'Sell',
      slLabel: sl == null ? '' : pipLabel('S/L', entry, sl, order.type),
      tpLabel: tp == null ? '' : pipLabel('T/P', entry, tp, order.type),
    })
  }

  // With several positions open at once (engine API 3, hedging) each set says whose it is —
  // "Buy #3", as the trades pane numbers them. One set needs no number. The number is part of
  // the key, so a set is redrawn when it gains or loses one.
  if (specs.length > 1) {
    for (const s of specs) {
      if (s.ticket == null) continue
      s.entryLabel = `${s.entryLabel} #${s.ticket}`
      s.key = `${s.key}:#${s.ticket}`
    }
  }

  // Newest first out of the budget: the trade the user is watching is the one that just
  // opened. In practice a handful of positions are open at once and this never binds.
  specs.sort((a, b) => a.entryTime - b.entryTime)
  return specs.length > budget ? specs.slice(specs.length - budget) : specs
}

interface PendingLineSpec {
  key: string
  placedTs: number
  point: { timestamp: number; value: number }
  dir: 'buy' | 'sell'
  label: string
}

/**
 * One dotted rule per order resting at the reveal cursor. The set is derived from the same
 * `restingOrdersAt` the trades pane's Pending tab uses, so a line and a row always agree.
 * Keyed by the order's ticket: its price never changes, so the overlay is created once when
 * the order is placed and removed once when it fills, cancels or expires.
 */
function buildPendingLines(
  candles: CandleData[],
  resting: PlacedOrderEvent[],
  precision: number,
  budget: number,
): PendingLineSpec[] {
  if (budget <= 0 || candles.length === 0 || resting.length === 0) return []
  const firstBarTime = candles[0].time
  const lastBarTime = candles[candles.length - 1].time
  const specs = resting.map((o): PendingLineSpec => {
    const anchor = Math.min(Math.max(o.time, firstBarTime), lastBarTime)
    return {
      key: `pending:${o.ticket}`,
      placedTs: o.time,
      point: { timestamp: anchor * 1000, value: o.price },
      dir: o.side === 'BUY' ? 'buy' : 'sell',
      label: `${PENDING_TYPE_ABBR[o.orderType]} ${o.price.toFixed(precision)} · ${o.volume.toFixed(2)}`,
    }
  })
  specs.sort((a, b) => a.placedTs - b.placedTs)
  return specs.length > budget ? specs.slice(specs.length - budget) : specs
}

/** One place the overlay's extendData is shaped, because two callers write it. */
function orderLineExtendData(
  spec: OrderLinesSpec,
  precision: number,
  dimmed: boolean,
): OrderLinesExtendData {
  return {
    dir: spec.dir,
    dimmed,
    hasSl: spec.slLabel !== '',
    hasTp: spec.tpLabel !== '',
    entryLabel: spec.entryLabel,
    slLabel: spec.slLabel,
    tpLabel: spec.tpLabel,
    precision,
  }
}

/** What a reconcile draws. */
export interface OrderLayerInput {
  /** The closed bars the chart shows, oldest first. */
  bars: CandleData[]
  /** The orders to draw, already cut at the reveal boundary (bar granularity). */
  orders: OrderData[]
  /**
   * The walkthrough's pending-order / netting events, cut like `orders`. Resting orders are
   * drawn as dotted rules, scale-ins and partial exits as small arrows; absent on the Backtest
   * tab, which then draws exactly what it always did.
   */
  activity?: OrderEventData[]
  timeframe: string
  /** The bar forming right after `bars`, while a replay animates it. */
  forming?: FormingBarState | null
  /** Sub-bar cursor, unix seconds: orders after it are held back. Infinity = bar granularity. */
  revealTs?: number
  /**
   * The walkthrough's rules: working lines for OPEN positions, and entry arrows kept grey until
   * their position closes — so a replay never tells the outcome at the entry.
   */
  liveOrderLines?: boolean
  /** From backtest_config.pip_size, for the lines' pip labels. */
  pipSize?: number | null
  /** Price decimals for the lines' axis tags. */
  precision: number
  walkthroughMarks?: WalkthroughMarks | null
  /**
   * position key (see pairKeyOf) -> ticket number, the trades pane's numbering. With it, the
   * working lines of several open positions say whose they are ("Buy #3"); without it, or with
   * one position open, they read as before.
   */
  tickets?: ReadonlyMap<PairKey, number>
}

/** Walkthrough arrows and × above the connector (0) between them. */
const MARK_Z = 1

/**
 * The pointer is over one of the walkthrough's marks (PROD-3133, as the prototype names them on
 * hover): a fill's arrow or ×, or a trade's connector. Null when it leaves.
 */
export type OrderHover = {
  kind: 'fill' | 'trade'
  order: OrderData
  /** Where the tip belongs, in chart terms (ms timestamp, price). */
  point: { timestamp: number; value: number }
} | null

/** A trade arrow was clicked: the orders grouped on it, and where it sits. */
export interface OrderMarkerClick {
  orders: OrderData[]
  /** Scale-ins / partial exits on the clicked arrow (empty for entry/exit arrows). */
  activity: OrderEventData[]
  /** Bar open, unix seconds. */
  barTime: number
  /** The arrow's anchor price (the bar's low for a buy, its high for a sell). */
  value: number
}

/**
 * The live overlay set for one chart, diffed on every reconcile.
 *
 * klinecharts iterates every overlay on each repaint, and a remove-all/create-all pass per replay
 * tick would crawl, so each set is keyed and only the delta is touched — in the replay steady
 * state that is zero or one overlay. Idempotent: calling it from two places (the data effect and
 * the forming bar) converges with no orphaned or duplicated overlays.
 */
export class OrderLayer {
  private readonly chart: Chart
  private readonly onMarkerClick: ((click: OrderMarkerClick) => void) | null
  private readonly onHover: ((hover: OrderHover) => void) | null
  private readonly onTap: ((hover: Exclude<OrderHover, null>) => void) | null
  // key -> overlay id, so each data change only touches the delta.
  private readonly markerIds = new Map<string, string>()
  private readonly connectorIds = new Map<string, string>()
  private readonly crossIds = new Map<string, string>()
  private readonly orderLineIds = new Map<string, string>()
  private readonly pendingLineIds = new Map<string, string>()
  /** The spec behind each live order-line overlay, so a dim toggle can rebuild extendData. */
  private orderLineSpecs = new Map<string, OrderLinesSpec>()
  /**
   * The order-line set currently lit, or null when every set is at full strength. Applied by
   * overriding the overlays' extendData in place, so it never needs a React render — which would
   * fight the 30fps forming-bar path.
   */
  private hoveredKey: string | null = null
  private precision = 5

  constructor(
    chart: Chart,
    opts: {
      onMarkerClick?: (click: OrderMarkerClick) => void
      onHover?: (hover: OrderHover) => void
      /**
       * A walkthrough connector or × was clicked or tapped. A phone has no hover, so a tap shows
       * what the hover would; the listener decides which pointer counts.
       */
      onTap?: (hover: Exclude<OrderHover, null>) => void
    } = {},
  ) {
    this.chart = chart
    this.onMarkerClick = opts.onMarkerClick ?? null
    this.onHover = opts.onHover ?? null
    this.onTap = opts.onTap ?? null
  }

  /** Hover handlers for one walkthrough mark; none on the Backtest tab or without a listener. */
  private hoverEvents(on: boolean, hover: Exclude<OrderHover, null>) {
    const cb = this.onHover
    if (!on || !cb) return {}
    return {
      onMouseEnter: () => {
        cb(hover)
        return false
      },
      onMouseLeave: () => {
        cb(null)
        return false
      },
    }
  }

  /** The click handler that passes a tap on a walkthrough mark on; none without a listener. */
  private tapEvents(hover: Exclude<OrderHover, null>) {
    const cb = this.onTap
    if (!cb) return {}
    return {
      onClick: () => {
        cb(hover)
        return true
      },
    }
  }

  reconcile(input: OrderLayerInput): void {
    const c = this.chart
    const { bars, timeframe, forming = null, revealTs = Infinity, liveOrderLines = false, pipSize = null } = input
    const wt = input.walkthroughMarks ?? null
    this.precision = input.precision
    // Infinity = bar granularity; the caller's reveal filter has already run.
    const orders = revealTs === Infinity ? input.orders : input.orders.filter((o) => o.time <= revealTs)
    const allActivity = input.activity ?? []
    const activity = revealTs === Infinity ? allActivity : allActivity.filter((a) => a.time <= revealTs)

    // Built from the already-reveal-filtered orders, so "closed" here means "closed as far as the
    // playhead knows" — which is what both the outcome colouring and the still-open test need.
    const closes = liveOrderLines ? closesByKey(orders) : null
    const orderLines = closes ? buildOrderLines(bars, orders, closes, pipSize, MAX_OVERLAYS, activity, input.tickets ?? null) : []
    // The activity is already cut at the reveal, so "resting" is relative to its end.
    const pendingLines = closes
      ? buildPendingLines(bars, restingOrdersAt(activity, Infinity), this.precision, MAX_OVERLAYS - orderLines.length)
      : []
    // Walkthrough: a trade's marks show once it has closed.
    const marked = wt && closes
      ? orders.filter((o) => {
          if (o.eventType === 'closed') return true
          const key = pairKeyOf(o)
          return key != null && closes.has(key)
        })
      : orders
    const markers = buildMarkers(bars, marked, timeframe, forming, closes, closes ? activity : [], wt)
    const connectors = buildConnectors(
      bars,
      orders,
      timeframe,
      MAX_OVERLAYS - markers.length - orderLines.length - pendingLines.length,
      forming,
      wt,
    )
    const crosses = wt
      ? buildCrosses(
          bars,
          marked,
          timeframe,
          MAX_OVERLAYS - markers.length - orderLines.length - pendingLines.length - connectors.length,
          forming,
        )
      : []

    const reconcile = (live: Map<string, string>, keys: Set<string>, create: (key: string) => void) => {
      for (const [key, id] of live) {
        if (!keys.has(key)) {
          c.removeOverlay(id)
          live.delete(key)
        }
      }
      for (const key of keys) {
        if (!live.has(key)) create(key)
      }
    }

    const markerByKey = new Map(markers.map((s) => [s.key, s]))
    reconcile(this.markerIds, new Set(markerByKey.keys()), (key) => {
      const spec = markerByKey.get(key)!
      const onClick = this.onMarkerClick
      const id = c.createOverlay({
        name: TRADE_MARKER,
        points: [{ timestamp: spec.barTime * 1000, value: spec.value }],
        extendData: { dir: spec.dir, color: spec.color, text: spec.text, mt5: spec.mt5 },
        // The walkthrough's arrows and × sit over the dashed line, as in the prototype, however the
        // reconcile happened to order their creation. Not below 0: that goes behind the candles.
        ...(spec.mt5 ? { zLevel: MARK_Z } : {}),
        ...this.hoverEvents(!!spec.mt5, { kind: 'fill', order: spec.orders[0], point: { timestamp: spec.barTime * 1000, value: spec.value } }),
        ...(onClick
          ? {
              onClick: () => {
                onClick({ orders: spec.orders, activity: spec.activity, barTime: spec.barTime, value: spec.value })
                return true
              },
            }
          : {}),
      })
      if (typeof id === 'string') this.markerIds.set(key, id)
    })

    const connectorByKey = new Map(connectors.map((s) => [s.key, s]))
    reconcile(this.connectorIds, new Set(connectorByKey.keys()), (key) => {
      const spec = connectorByKey.get(key)!
      const id = c.createOverlay({
        name: TRADE_CONNECTOR,
        points: spec.points,
        extendData: { color: spec.color, size: spec.size, tipGap: spec.tipGap, long: spec.long, fromExit: spec.fromExit },
        ...this.hoverEvents(!!spec.tipGap, { kind: 'trade', order: spec.order, point: spec.points[0] }),
        ...(spec.tipGap ? this.tapEvents({ kind: 'trade', order: spec.order, point: spec.points[0] }) : {}),
      })
      if (typeof id === 'string') this.connectorIds.set(key, id)
    })

    // The theme colour is in the key, so a theme switch redraws them like any other change.
    const crossByKey = new Map(crosses.map((s) => [`${s.key}:${wt?.cross ?? ''}:${wt?.halo ?? ''}`, s]))
    reconcile(this.crossIds, new Set(crossByKey.keys()), (key) => {
      const spec = crossByKey.get(key)!
      const id = c.createOverlay({
        name: FILL_CROSS,
        points: [spec.point],
        extendData: { color: wt?.cross, halo: wt?.halo },
        zLevel: MARK_Z,
        ...this.hoverEvents(true, { kind: 'fill', order: spec.order, point: spec.point }),
        ...this.tapEvents({ kind: 'fill', order: spec.order, point: spec.point }),
      })
      if (typeof id === 'string') this.crossIds.set(key, id)
    })

    // The live sets last. A trade that just closed loses its lines in the same pass that
    // recolours its arrows, so the two never disagree for a frame.
    const orderLineByKey = new Map(orderLines.map((s) => [s.key, s]))
    // Replaced wholesale rather than mutated, so a closed trade's spec is pruned for free.
    this.orderLineSpecs = orderLineByKey
    const liveKeys = new Set(orderLineByKey.keys())
    // A hovered set that has since closed leaves nothing to un-dim, so drop the lit key with it
    // rather than leaving every other set faded with no way back.
    if (this.hoveredKey && !liveKeys.has(this.hoveredKey)) this.hoveredKey = null
    reconcile(this.orderLineIds, liveKeys, (key) => {
      const spec = orderLineByKey.get(key)!
      const id = c.createOverlay({
        name: ORDER_LINES,
        points: spec.points,
        // The dim flag is seeded from the live hover state: a trade opening while another one is
        // lit has to arrive already faded, or it would be the only set at full strength.
        extendData: orderLineExtendData(
          spec,
          this.precision,
          this.hoveredKey !== null && this.hoveredKey !== key,
        ),
        onMouseEnter: () => {
          this.hoveredKey = key
          this.applyOrderLineDim()
          return false
        },
        onMouseLeave: () => {
          if (this.hoveredKey !== key) return false
          this.hoveredKey = null
          this.applyOrderLineDim()
          return false
        },
      })
      if (typeof id === 'string') this.orderLineIds.set(key, id)
    })

    // Resting orders: created when placed, removed when they fill, cancel or expire.
    const pendingByKey = new Map(pendingLines.map((s) => [s.key, s]))
    reconcile(this.pendingLineIds, new Set(pendingByKey.keys()), (key) => {
      const spec = pendingByKey.get(key)!
      const extendData: PendingLineExtendData = { dir: spec.dir, label: spec.label, precision: this.precision }
      const id = c.createOverlay({ name: PENDING_ORDER_LINE, points: [spec.point], extendData })
      if (typeof id === 'string') this.pendingLineIds.set(key, id)
    })

    const total = markers.length + connectors.length + crosses.length + orderLines.length + pendingLines.length
    if (import.meta.env.DEV && total >= MAX_OVERLAYS) {
      console.warn(
        `[OrderLayer] overlays capped at ${MAX_OVERLAYS} ` +
          `(${markers.length} markers + ${connectors.length} connectors + ${crosses.length} crosses + ` +
          `${orderLines.length} order lines + ${pendingLines.length} pending lines); ` +
          `older trades in this window are not drawn`,
      )
    }
  }

  /** Remove every overlay this layer drew. */
  clear(): void {
    for (const ids of [this.markerIds, this.connectorIds, this.crossIds, this.orderLineIds, this.pendingLineIds]) {
      for (const id of ids.values()) this.chart.removeOverlay(id)
    }
    this.forget()
  }

  /** Drop the bookkeeping without touching the chart (it is being disposed, or wiped). */
  forget(): void {
    this.markerIds.clear()
    this.connectorIds.clear()
    this.crossIds.clear()
    this.orderLineIds.clear()
    this.pendingLineIds.clear()
    this.orderLineSpecs = new Map()
    this.hoveredKey = null
  }

  /**
   * Push the current hover state onto the live order-line overlays.
   *
   * Deliberately `overrideOverlay` rather than remove-and-recreate. An overlay that is destroyed
   * can never deliver its own `mouseleave`, so rebuilding the set in order to light one of them
   * loses the un-hover for the very overlay the pointer is sitting on — and the lit set stays lit
   * with no way back to dim. `overrideOverlay` REPLACES extendData rather than merging it, so the
   * whole object is rebuilt from the spec every time; that is why the specs are kept.
   */
  private applyOrderLineDim(): void {
    const lit = this.hoveredKey
    for (const [key, id] of this.orderLineIds) {
      const spec = this.orderLineSpecs.get(key)
      if (!spec) continue
      this.chart.overrideOverlay({
        id,
        extendData: orderLineExtendData(spec, this.precision, lit !== null && lit !== key),
      })
    }
  }
}
